import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import { AuthInterceptor } from '@interceptors/auth.interceptor';
import { WindowWithUser } from '@longnecktech/splash-commons-fe';
import { GameService } from '@services/game.service';
import { SessionService } from '@services/session.service';
import { registerLocaleData, CurrencyPipe } from '@angular/common';
import { SpinService } from '@services/spin.service';
import { TranslationService } from '@services/translation.service';
import { getTokenParams } from '@shared/utils/get-token-params';
import localeDe from '@angular/common/locales/de';
import localeHr from '@angular/common/locales/hr';
import localeEn from '@angular/common/locales/en';
import localeSe from '@angular/common/locales/se';
import localePt from '@angular/common/locales/pt';
import localeFr from '@angular/common/locales/fr';
import localeSv from '@angular/common/locales/sv';
import localeEs from '@angular/common/locales/es';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web/build/player/lottie_svg';
import { firstValueFrom } from 'rxjs';

import * as Sentry from '@sentry/angular';
import { GlobalErrorHandler } from '@shared/errors/global-error-handler';
import { apiErrorInterceptor } from '@interceptors/api-error.interceptor';

import { APP_ROUTES } from './app.routes';
import { previewInterceptor } from './features/wheel-store/preview.fixture';
import { isEngineWheel } from './features/engine/engine-route';
import { EngineService } from './features/engine/engine.service';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeHr, 'hr');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeSe, 'se');

export function localeFactory() {
  const lang = (window as WindowWithUser).user?.language || 'en';
  return TranslationService.supportedLanguages.includes(lang) ? lang : 'en';
}

export function startupServiceFactory(
  sessionService: SessionService,
  gameService: GameService,
  spinService: SpinService,
  engineService: EngineService,
) {
  return async () => {
    if (isEngineWheel()) {
      try {
        await firstValueFrom(gameService.fetchGame());
        spinService.setSpinInfo({
          allowedToSpin: true,
          nextSpinInSeconds: 0,
          lastSpinTransaction: null,
          startTs: Date.now(),
        });
      } catch (error) {
        engineService.setError(error);
      }
      return;
    }
    const params = getTokenParams();
    sessionService.thirdPartyToken = params.token;
    sessionService.instance = params.instance;
    sessionService.gameUuid = params.gameUuid;
    sessionService.isFromHub = params.isFromHub;
    sessionService.hubGameUuid = params.hubGameUuid;
    sessionService.setUser((window as WindowWithUser).user);
    const game = await firstValueFrom(gameService.fetchGame());
    // get spin info only if game is active and has started
    if (game.uuid && !game.ended && !game.paused) {
      await firstValueFrom(spinService.fetchSpinInfo(game.uuid));
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(APP_ROUTES),
    provideAnimations(),
    CurrencyPipe,
    provideAppInitializer(() => {
      const initializerFn = startupServiceFactory(
        inject(SessionService),
        inject(GameService),
        inject(SpinService),
        inject(EngineService),
      );
      return initializerFn();
    }),
    {
      provide: LOCALE_ID,
      useFactory: localeFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    provideLottieOptions({
      player: () => player,
    }),
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [Sentry.TraceService],
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([previewInterceptor, apiErrorInterceptor]),
    ),
  ],
};
